import { useEffect, useState } from 'react';
import { Shield, AlertTriangle, Search, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

interface RiskOrg {
  organization_id: string;
  name: string;
  risk_score: number;
  last_research_date: string | null;
  report_status: string | null;
  report_expires_at: string | null;
}

interface KameraReport {
  id: number;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  risk_score: number;
  key_findings: { category: string; finding: string; severity: string }[];
  narrative: string;
  report_date: string | null;
  expires_at: string | null;
  status: string;
}

function riskColor(score: number): string {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#22c55e';
}

function riskLabel(score: number): string {
  if (score >= 70) return 'High';
  if (score >= 40) return 'Medium';
  return 'Low';
}

export default function IntelligencePage() {
  const [orgs, setOrgs] = useState<RiskOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [report, setReport] = useState<KameraReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [researching, setResearching] = useState<string | null>(null);

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/intelligence/risks');
      setOrgs(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const triggerResearch = async (orgId: string) => {
    setResearching(orgId);
    try {
      const res = await api.post(`/intelligence/research/org/${orgId}`);
      if (res.data.report_id) {
        await viewReport(res.data.report_id);
        setExpandedOrg(orgId);
      }
      await fetchOrgs();
    } catch {
      /* ignore */
    } finally {
      setResearching(null);
    }
  };

  const viewReport = async (reportId: number) => {
    setReportLoading(true);
    try {
      const res = await api.get(`/intelligence/report/${reportId}`);
      setReport(res.data);
    } catch {
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Shield size={24} style={{ color: 'var(--netrun-green)' }} />
          KAMERA Intelligence
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          OSINT research and risk scoring for organizations and contacts
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>Loading risk data...</div>
      ) : orgs.length === 0 ? (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>
          <Shield size={48} className="mx-auto mb-3 opacity-30" />
          <p>No organizations have been researched yet.</p>
          <p className="text-xs mt-1">Go to an organization and trigger a KAMERA research scan.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orgs.map((org) => {
            const isExpanded = expandedOrg === org.organization_id;
            return (
              <div
                key={org.organization_id}
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
              >
                {/* Org Row */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer"
                  onClick={() => setExpandedOrg(isExpanded ? null : org.organization_id)}
                >
                  {/* Risk Badge */}
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold"
                    style={{ background: `${riskColor(org.risk_score)}20`, color: riskColor(org.risk_score) }}
                  >
                    {org.risk_score}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{org.name}</div>
                    <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <span
                        className="px-2 py-0.5 rounded-full"
                        style={{ background: `${riskColor(org.risk_score)}20`, color: riskColor(org.risk_score) }}
                      >
                        {riskLabel(org.risk_score)} Risk
                      </span>
                      {org.last_research_date && (
                        <span className="flex items-center gap-1">
                          <Clock size={10} />
                          Researched {new Date(org.last_research_date).toLocaleDateString()}
                        </span>
                      )}
                      {org.report_status === 'mock' && (
                        <span className="flex items-center gap-1 text-amber-500">
                          <AlertTriangle size={10} /> Pending API integration
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); triggerResearch(org.organization_id); }}
                    disabled={researching === org.organization_id}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium"
                    style={{
                      background: researching === org.organization_id ? 'var(--bg-tertiary)' : 'var(--netrun-green)',
                      color: researching === org.organization_id ? 'var(--text-muted)' : '#000',
                    }}
                  >
                    <Search size={12} className="inline mr-1" />
                    {researching === org.organization_id ? 'Researching...' : 'Re-scan'}
                  </button>

                  {isExpanded ? (
                    <ChevronUp size={16} style={{ color: 'var(--text-muted)' }} />
                  ) : (
                    <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />
                  )}
                </div>

                {/* Expanded Report */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="border-t pt-4" style={{ borderColor: 'var(--border-subtle)' }}>
                      {reportLoading ? (
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading report...</p>
                      ) : report && report.entity_id === org.organization_id ? (
                        <div className="space-y-3">
                          {report.key_findings.length > 0 && (
                            <div>
                              <h3 className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--text-muted)' }}>
                                Key Findings
                              </h3>
                              <div className="space-y-1">
                                {report.key_findings.map((f, i) => (
                                  <div key={i} className="flex items-start gap-2 text-sm">
                                    <span
                                      className="mt-1 w-2 h-2 rounded-full flex-shrink-0"
                                      style={{
                                        background:
                                          f.severity === 'high' ? '#ef4444' :
                                          f.severity === 'medium' ? '#f59e0b' : '#6b7280',
                                      }}
                                    />
                                    <span style={{ color: 'var(--text-secondary)' }}>{f.finding}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {report.narrative && (
                            <div>
                              <h3 className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--text-muted)' }}>
                                Narrative
                              </h3>
                              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                                {report.narrative}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                          Click "Re-scan" to generate a report.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
